import { getSamAssets, fetchPresignedJson } from '@/api/RobVisionApi';
import type { SamEmbeddingMeta } from '@/models/RobVision';

/**
 * App-wide SAM decoder session. The worker downloads ~20 MB on first start
 * (decoder ONNX, positional embedding, wasm runtime), so it is warmed as
 * soon as a project with real-world images is opened and survives editor
 * closes — later editor sessions attach instantly. Idle cost is one worker
 * thread and the loaded decoder session; per-image embeddings are cleared
 * on every image open, so memory does not grow with use.
 */

let worker: Worker | null = null;
let ready: Promise<boolean> | null = null;

export function samDecoderWorker(): Worker | null {
  return worker;
}

/**
 * Start (or reuse) the decoder worker. Resolves true once the worker posted
 * 'ready'. Failures (assets not published, worker/wasm breakage) resolve
 * false and reset the session so a later call retries.
 */
export function warmSamDecoder(): Promise<boolean> {
  if (ready) {
    return ready;
  }
  ready = (async () => {
    // The positional embedding lives in a global S3 location; without the
    // assets SAM assist stays unavailable but everything else works. Its
    // meta JSON names the encoder checkpoint, versioning the worker cache.
    let assets;
    let modelName = '';
    try {
      assets = await getSamAssets();
      const meta = await fetchPresignedJson<SamEmbeddingMeta>(
        assets.positionalEmbeddingMetaUrl,
      );
      modelName = meta.model_name;
    } catch (error) {
      console.error('SAM assets unavailable — assist disabled:', error);
      return false;
    }
    worker = new Worker(
      new URL('@/workers/sam-decoder-worker.ts', import.meta.url),
      { type: 'module' },
    );
    const startedWorker = worker;
    const initialized = new Promise<boolean>((resolve) => {
      // A worker that fails to even start (asset 404 in a prod build, wasm
      // loader breakage) never posts a message — without these the promise
      // pends forever and the UI hangs in "loading".
      startedWorker.onerror = (event) => {
        console.error('SAM worker failed:', event.message);
        resolve(false);
      };
      setTimeout(() => resolve(false), 120_000);
      const onInitMessage = (e: MessageEvent) => {
        if (e.data.type === 'ready') {
          startedWorker.removeEventListener('message', onInitMessage);
          resolve(true);
        } else if (e.data.type === 'error') {
          startedWorker.removeEventListener('message', onInitMessage);
          console.error('SAM decoder init failed:', e.data.data?.message);
          resolve(false);
        }
      };
      startedWorker.addEventListener('message', onInitMessage);
    });
    startedWorker.postMessage({
      type: 'init',
      data: {
        positionalEmbeddingUrl: assets.positionalEmbeddingUrl,
        modelName,
      },
    });
    return initialized;
  })();
  void ready.then((ok) => {
    if (!ok) {
      worker?.terminate();
      worker = null;
      ready = null;
    }
  });
  return ready;
}
